import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectAPI } from '../services/api';

/**
 * /join/:code — "join my run" companion entry (plan_companion_join.md).
 *
 * The host shows a QR / link from inside their session; anyone opening it
 * lands in the SAME project as a participant with their own private chat.
 * - Registered users (and existing guests) redeem the code directly.
 * - Brand-new visitors are minted as guests first (partner runs count against
 *   the venue budget; B2C runs are sponsored from the host's balance).
 */
const JoinPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, startGuestSession } = useAuth();
  const [error, setError] = useState(null);
  const startedRef = useRef(false);
  const guestAttemptedRef = useRef(false);

  useEffect(() => {
    if (isLoading || startedRef.current || error) return;

    if (!isAuthenticated) {
      if (guestAttemptedRef.current) return;
      guestAttemptedRef.current = true;

      const createGuest = async () => {
        const result = await startGuestSession({ joinCode: code });
        if (!result.success) {
          setError(messageForDetail(result.message, result.status));
        }
        // On success isAuthenticated flips and this effect re-runs into redeem
      };
      createGuest();
      return;
    }

    startedRef.current = true;

    const redeem = async () => {
      try {
        const response = await projectAPI.redeemJoinCode(code);
        const { projectId, chatId } = response.data.data;
        navigate(`/projects/${projectId}/chats/${chatId}`, { replace: true });
      } catch (err) {
        console.error('[JoinPage] Failed to redeem join code:', err);
        startedRef.current = false;
        setError(
          messageForDetail(err?.response?.data?.detail, err?.response?.status)
        );
      }
    };

    redeem();
  }, [isLoading, isAuthenticated, code, navigate, startGuestSession, error]);

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center px-8 text-center">
      {!error ? (
        <>
          <div className="w-12 h-12 rounded-full border-2 border-emerald-400/30 border-t-emerald-400 animate-spin mb-6" />
          <h1 className="text-white text-xl font-semibold mb-2">
            Joining the adventure…
          </h1>
          <p className="text-white/50 text-sm max-w-xs">
            One moment — we're adding you to your group's run.
          </p>
        </>
      ) : (
        <>
          <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-400/30 flex items-center justify-center mb-6">
            <span className="text-red-400 text-xl">!</span>
          </div>
          <h1 className="text-white text-xl font-semibold mb-2">
            Couldn't join
          </h1>
          <p className="text-white/50 text-sm max-w-xs mb-6">{error}</p>
          <button
            onClick={() => {
              setError(null);
              startedRef.current = false;
              guestAttemptedRef.current = false;
              window.location.reload();
            }}
            className="px-6 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
          >
            Retry
          </button>
        </>
      )}
    </div>
  );
};

const messageForDetail = (detail, statusCode) => {
  switch (detail) {
    case 'join_code_invalid':
      return 'This invite link has expired or is no longer valid. Ask your host to share a fresh one from inside their session.';
    case 'companion_join_disabled':
      return 'The venue has turned off group joining for this experience.';
    case 'run_full':
      return "This run is full — your group has reached the maximum number of companions.";
    case 'host_out_of_tokens':
      return "Your host's balance can't cover another companion right now.";
    case 'venue_budget_exhausted':
      return 'This experience is very busy right now — please try again later.';
    case 'starter_offline':
      return 'This experience is taking a short break — the venue has paused it. Please check back soon!';
    case 'join_unavailable':
      return "This run can't take companions right now. Ask your host to share a fresh invite.";
    default:
      if (statusCode === 429) {
        return 'Too many new sessions from this connection. Please wait a minute and try again.';
      }
      return 'Could not join the run. Check your connection and tap retry.';
  }
};

export default JoinPage;
